import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../utils/api";
import "./PhoneVerificationModal.css";

interface PhoneVerificationModalProps {
  isOpen: boolean;
  phoneNumber: string;
  onVerified: () => void;
  onCancel: () => void;
  onPhoneTakenByVehicleOwner?: () => void;
}

interface OtpResponse {
  ok: boolean;
  error: string | null;
  token: string | null;
}

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_NUMBER: "Invalid phone number format",
  RATE_LIMITED: "Too many attempts. Please try again later.",
  DELIVERY_FAILED: "Failed to send code. Please try again.",
  EXPIRED_CODE: "Code expired. Please request a new code.",
  INVALID_CODE: "Incorrect code. Please try again.",
  LOCKED: "Too many incorrect attempts. Please try again later.",
  PHONE_TAKEN_VEHICLEOWNER: "This phone is linked to an existing app account.",
};

const RESEND_COOLDOWN_SECONDS = 30;

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
  isOpen,
  phoneNumber,
  onVerified,
  onCancel,
  onPhoneTakenByVehicleOwner,
}) => {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(RESEND_COOLDOWN_SECONDS);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpRequestInFlight = useRef(false);

  // Normalize phone for API calls (remove dashes and formatting)
  const normalizePhoneForApi = useCallback((phone: string): string => {
    return phone.replace(/\D/g, "");
  }, []);

  // Get last 4 digits for display
  const getLastFourDigits = useCallback((phone: string): string => {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.slice(-4);
  }, []);

  // Start OTP verification
  const sendOtp = useCallback(async () => {
    // Guard against duplicate requests (especially from StrictMode)
    if (otpRequestInFlight.current) {
      return;
    }

    otpRequestInFlight.current = true;
    setIsSendingCode(true);
    setError(null);

    try {
      const response = await api.post<OtpResponse>(
        "/auth/otp/start",
        {
          phone: normalizePhoneForApi(phoneNumber),
        },
        {
          // OTP is requested before the user is fully logged in, so skip auth middleware
          skipAuthRedirect: true,
        } as any
      );

      if (!response.data.ok) {
        const errorCode = response.data.error || "DELIVERY_FAILED";
        setError(ERROR_MESSAGES[errorCode] || "Failed to send verification code");
      } else {
        // Start cooldown timer
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err: any) {
      console.error("Error sending OTP:", err);

      // Check if the error indicates phone is taken by a VehicleOwner
      if (err.response?.status === 409 || err.response?.status === 400) {
        const errorData = err.response.data;
        const existingRole = errorData?.existingAccountRole || errorData?.data?.existingAccountRole;

        if (existingRole === 'VehicleOwner' && onPhoneTakenByVehicleOwner) {
          // Phone is taken by a VehicleOwner - offer to link accounts
          onPhoneTakenByVehicleOwner();
          return;
        }

        // Phone taken by non-VehicleOwner or no callback provided
        if (errorData?.message?.toLowerCase().includes('phone') ||
            errorData?.message?.toLowerCase().includes('taken') ||
            errorData?.message?.toLowerCase().includes('exists')) {
          setError("This phone number is already in use.");
        } else {
          setError(errorData?.message || "Failed to send verification code. Please try again.");
        }
      } else {
        setError("Failed to send verification code. Please try again.");
      }
    } finally {
      setIsSendingCode(false);
      otpRequestInFlight.current = false;
    }
  }, [phoneNumber, normalizePhoneForApi, onPhoneTakenByVehicleOwner]);

  // Verify OTP code
  const verifyOtp = useCallback(async (code: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<OtpResponse>(
        "/auth/otp/verify",
        {
          phone: normalizePhoneForApi(phoneNumber),
          code,
        },
        {
          skipAuthRedirect: true,
        } as any
      );

      if (response.data.ok) {
        onVerified();
      } else {
        const errorCode = response.data.error || "INVALID_CODE";
        setError(ERROR_MESSAGES[errorCode] || "Verification failed");
        // Clear digits on error so user can retry
        setDigits(["", "", "", "", "", ""]);
        // Focus first input
        inputRefs.current[0]?.focus();
      }
    } catch (err: any) {
      console.error("Error verifying OTP:", err);
      setError("Verification failed. Please try again.");
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, normalizePhoneForApi, onVerified]);

  // Send OTP on mount
  useEffect(() => {
    if (isOpen) {
      sendOtp();
      // Focus first input after a short delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }

    return () => {
      // Reset state when modal closes
      setDigits(["", "", "", "", "", ""]);
      setError(null);
      setIsLoading(false);
      setIsSendingCode(false);
    };
  }, [isOpen, sendOtp]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [cooldownSeconds]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onCancel]);

  // Handle digit input
  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, "").slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError(null);

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all 6 digits entered
    if (digit && index === 5) {
      const code = newDigits.join("");
      if (code.length === 6) {
        verifyOtp(code);
      }
    }
  };

  // Handle key down for backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);

    if (pastedData.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      setDigits(newDigits);
      setError(null);

      // Focus appropriate input or verify
      if (pastedData.length === 6) {
        verifyOtp(pastedData);
      } else {
        inputRefs.current[pastedData.length]?.focus();
      }
    }
  };

  // Handle resend
  const handleResend = () => {
    if (cooldownSeconds === 0 && !isSendingCode) {
      setDigits(["", "", "", "", "", ""]);
      sendOtp();
      inputRefs.current[0]?.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="phone-verification-overlay">
      <div className="phone-verification-modal">
        <div className="phone-verification-close" onClick={onCancel}>
          <span className="phone-verification-cancel-text">Cancel (ESC)</span>
          <img src="/assets/x.svg" alt="Close" className="phone-verification-close-icon" />
        </div>

        <h2>Verify Phone</h2>

        <p className="phone-verification-description">
          Enter the 6-digit code sent to<br />
          <span className="phone-verification-masked">****{getLastFourDigits(phoneNumber)}</span>
        </p>

        {isSendingCode && (
          <p className="phone-verification-sending">Sending verification code...</p>
        )}

        <div className="phone-verification-inputs">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isLoading || isSendingCode}
              className={`phone-verification-digit ${error ? "error" : ""} ${digit ? "filled" : ""}`}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        {isLoading && (
          <p className="phone-verification-verifying">Verifying...</p>
        )}

        {error && (
          <p className="phone-verification-error">{error}</p>
        )}

        <div className="phone-verification-resend">
          <span className="phone-verification-resend-label">Didn't receive a code?</span>
          {cooldownSeconds > 0 ? (
            <span className="phone-verification-cooldown">Send in {cooldownSeconds}s</span>
          ) : (
            <button
              type="button"
              className="phone-verification-resend-button"
              onClick={handleResend}
              disabled={isSendingCode}
            >
              Resend Code
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
